import axios, { AxiosResponse } from 'axios';

import { getConfig } from '../config';
import { Forum, QuotedThread, Thread, ThreadMediaType, Topic } from '../database/forum';
import { PresignedUrlResponse, SetLikeRequest } from './theSourceApi';
import { ThreadSortOrder } from '../interstellar/TopicDetailPage';

const BASE_URL = getConfig().api.baseUrl;

export type ForumApiContextType = {
    /**
     * createForum saves the provided forum in the database.
     * @param req The CreateForumRequest.
     * @returns The newly created forum.
     */
    createForum: (req: CreateForumRequest) => Promise<AxiosResponse<Forum, any>>;

    /**
     * listForums returns a list of forums.
     * @param startKey The optional startKey to use for pagination.
     * @returns A list of forums.
     */
    listForums: (startKey?: string) => Promise<AxiosResponse<ListForumsResponse, any>>;

    /**
     * listTopics returns a list of topics for a specified forum
     * @param forumId The sk of the forum
     * @param startKey The optional startKey to use for pagination
     * @returns A list of topics for a specified forum
     */
    listTopics: (
        forumId: string,
        startKey?: string,
    ) => Promise<AxiosResponse<ListTopicsResponse, any>>;

    /**
     * createTopic creates a new topic in the specified forum
     * @param forumSk The sk of the forum
     * @param req The {@link CreateTopicRequest}
     * @returns The newly created topic
     */
    createTopic(
        forumSk: string,
        req: CreateTopicRequest,
    ): Promise<AxiosResponse<CreateTopicResponse, any>>;

    /**
     * putPresignedUrl generates a presigned URL for uploading media to a thread
     * @param forumSk Unique identifier for the forum
     * @param topicSk Unique identifier for the topic
     * @param threadSk Unique identifier for the thread
     * @param contentType The content type of the media
     */
    putPresignedUrl(
        forumSk: string,
        topicSk: string,
        threadSk: string,
        contentType: string,
    ): Promise<AxiosResponse<PresignedUrlResponse, any>>;

    /**
     * listThreads returns a list of threads for a specified topic
     * @param topicSk The sk of the topic
     * @param sortOrder The order to sort the threads
     * @param startKey The optional startKey to use for pagination
     * @returns A list of threads for a specified topic
     */
    listThreads: (
        topicSk: string,
        sortOrder: ThreadSortOrder,
        startKey?: string,
    ) => Promise<AxiosResponse<ListThreadsResponse, any>>;

    /**
     * createThread creates a new thread in the specified topic
     * @param forumSk The sk of the forum
     * @param topicSk The sk of the topic
     * @param req The {@link CreateThreadRequest}
     * @returns The newly created thread
     */
    createThread(
        forumSk: string,
        topicSk: string,
        req: CreateThreadRequest,
    ): Promise<AxiosResponse<Thread, any>>;

    /**
     * setThreadLike sets or removes a like on a thread
     * @param topicId The id of the topic
     * @param threadId The id of the thread
     * @param req The SetLikeRequest that contains if the thread is liked
     * @returns The updated thread
     */
    setThreadLike(
        topicId: string,
        threadId: string,
        req: SetLikeRequest,
    ): Promise<AxiosResponse<Thread, any>>;

    /**
     * deleteThread deletes a thread
     * @param forumId The id of the forum
     * @param topicId The id of the topic
     * @param threadId The id of the thread
     * @param owner The owner of the thread
     */
    deleteThread(
        forumId: string,
        topicId: string,
        threadId: string,
        owner: string,
    ): Promise<AxiosResponse<Thread, any>>;

    /**
     * deleteTopic deletes a topic
     * @param forumId The id of the forum
     * @param topicId The id of the topic
     */
    deleteTopic(forumId: string, topicId: string): Promise<AxiosResponse<Topic, any>>;

    /**
     * getTopic fetches a single topic by its pk (forumId) and sk (topicId).
     * @param forumId The pk of the topic (forum sk).
     * @param topicId The sk of the topic.
     * @returns The topic.
     */
    getTopic(forumId: string, topicId: string): Promise<AxiosResponse<Topic, any>>;

    /**
     * getThread fetches a single thread by its pk (topicId) and sk (threadId).
     * @param topicId The pk of the thread (topic sk).
     * @param threadId The sk of the thread.
     * @returns The thread.
     */
    getThread(topicId: string, threadId: string): Promise<AxiosResponse<Thread, any>>;
};

export interface CreateForumRequest {
    name: string;
    description: string;
}

export interface ListForumsResponse {
    forums: Forum[];
    lastEvaluatedKey?: string;
}

export interface ListTopicsResponse {
    topics: Topic[];
    lastEvaluatedKey?: string;
}

export interface CreateInitialThreadRequest {
    contentText?: string;
    mediaType?: ThreadMediaType;
}

export interface CreateTopicRequest {
    title: string;
    thread: CreateInitialThreadRequest;
}

export interface CreateTopicResponse {
    topic: Topic;
    threadId: string;
}

export interface ListThreadsResponse {
    threads: Thread[];
    lastEvaluatedKey?: string;
}

export interface CreateThreadRequest {
    contentText?: string;
    mediaType?: ThreadMediaType;
    replyToThreadId?: string;
    quotedThread?: QuotedThread;
}

export function createForum(idToken: string, req: CreateForumRequest) {
    return axios.post<Forum>(BASE_URL + '/forum', req, {
        headers: {
            Authorization: 'Bearer ' + idToken,
        },
    });
}

export function listForums(startKey?: string) {
    return axios.get<ListForumsResponse>(BASE_URL + '/public/forums', {
        params: { startKey },
    });
}

export function listTopics(forumSk: string, startKey?: string) {
    const encodedSk = encodeURIComponent(forumSk);
    return axios.get<ListTopicsResponse>(BASE_URL + `/public/topics/${encodedSk}`, {
        params: { startKey },
    });
}

export function createTopic(idToken: string, forumSk: string, req: CreateTopicRequest) {
    const encodedSk = encodeURIComponent(forumSk);
    return axios.post<CreateTopicResponse>(BASE_URL + `/forum/${encodedSk}/topic`, req, {
        headers: {
            Authorization: 'Bearer ' + idToken,
        },
    });
}

export function putPresignedUrl(
    idToken: string,
    forumSk: string,
    topicSk: string,
    threadSk: string,
    contentType: string,
) {
    const encodedForumSk = encodeURIComponent(forumSk);
    const encodedTopicSk = encodeURIComponent(topicSk);
    const encodedThreadSk = encodeURIComponent(threadSk);
    return axios.post<PresignedUrlResponse>(
        BASE_URL +
            `/forum/${encodedForumSk}/topic/${encodedTopicSk}/thread/${encodedThreadSk}/presignUrl`,
        { contentType },
        {
            headers: {
                Authorization: 'Bearer ' + idToken,
            },
        },
    );
}

export function listThreads(
    topicSk: string,
    sortOrder: ThreadSortOrder,
    startKey?: string,
) {
    const encodedTopicSk = encodeURIComponent(topicSk);
    return axios.get<ListThreadsResponse>(
        BASE_URL + `/public/topic/${encodedTopicSk}/sort/${sortOrder}/threads`,
        {
            params: { startKey },
        },
    );
}

export function createThread(
    idToken: string,
    forumSk: string,
    topicSk: string,
    req: CreateThreadRequest,
) {
    const encodedForumSk = encodeURIComponent(forumSk);
    const encodedTopicSk = encodeURIComponent(topicSk);
    return axios.post<Thread>(
        BASE_URL + `/forum/${encodedForumSk}/topic/${encodedTopicSk}/thread`,
        req,
        {
            headers: {
                Authorization: 'Bearer ' + idToken,
            },
        },
    );
}

export function setThreadLike(
    idToken: string,
    topicId: string,
    threadId: string,
    req: SetLikeRequest,
) {
    const encodedTopicId = encodeURIComponent(topicId);
    const encodedThreadId = encodeURIComponent(threadId);
    return axios.put<Thread>(
        BASE_URL + `/topic/${encodedTopicId}/thread/${encodedThreadId}/like`,
        req,
        {
            headers: {
                Authorization: 'Bearer ' + idToken,
            },
        },
    );
}

export function deleteThread(
    idToken: string,
    forumId: string,
    topicId: string,
    threadId: string,
    owner: string,
) {
    const encodedForumId = encodeURIComponent(forumId);
    const encodedTopicId = encodeURIComponent(topicId);
    const encodedThreadId = encodeURIComponent(threadId);
    const encodedOwner = encodeURIComponent(owner);
    return axios.delete<Thread>(
        BASE_URL +
            `/forum/${encodedForumId}/topic/${encodedTopicId}/thread/${encodedThreadId}/owner/${encodedOwner}`,
        {
            headers: {
                Authorization: 'Bearer ' + idToken,
            },
        },
    );
}

export function deleteTopic(idToken: string, forumId: string, topicId: string) {
    const encodedForumId = encodeURIComponent(forumId);
    const encodedTopicId = encodeURIComponent(topicId);
    return axios.delete<Topic>(
        BASE_URL + `/forum/${encodedForumId}/topic/${encodedTopicId}`,
        {
            headers: {
                Authorization: 'Bearer ' + idToken,
            },
        },
    );
}

export function getTopic(forumId: string, topicId: string) {
    const encodedForumId = encodeURIComponent(forumId);
    const encodedTopicId = encodeURIComponent(topicId);
    return axios.get<Topic>(
        BASE_URL + `/public/forum/${encodedForumId}/topic/${encodedTopicId}`,
    );
}

export function getThread(topicId: string, threadId: string) {
    const encodedTopicId = encodeURIComponent(topicId);
    const encodedThreadId = encodeURIComponent(threadId);
    return axios.get<Thread>(
        BASE_URL + `/public/topic/${encodedTopicId}/thread/${encodedThreadId}`,
    );
}
